import { ChevronDownIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Country } from 'react-phone-number-input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandList } from '../../../Command';
import { Popover, PopoverTrigger, PopoverContent } from '../../../Popover';
import { ScrollArea } from '../../../ScrollArea';
import { FlagComponent } from '../FlagComponent';
import { Option } from './Option';
import * as Styles from './styles';
import type { CountrySelectProps } from './types';

export const CountrySelect = ({
  disabled,
  readOnly = false,
  value: selectedCountry,
  options: countryList,
  onChange,
  variant,
}: CountrySelectProps): React.ReactElement => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const validCountries = countryList.filter(({ value }) => value);
  const { t } = useTranslation(LOCIZE_NS.COMMON);

  const handleOpenChange = (open: boolean): void => {
    if (readOnly) return;
    setIsOpen(open);
    if (open) {
      setSearchValue('');
    }
  };

  return (
    <Popover open={readOnly ? false : isOpen} modal onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-has-value={readOnly ? undefined : !!selectedCountry}
          data-readonly={readOnly}
          aria-disabled={readOnly}
          className={cn(
            Styles.countrySelectTrigger(variant),
            readOnly && Styles.countrySelectReadOnlyTrigger
          )}
          disabled={disabled}
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry} />
          <ChevronDownIcon
            strokeWidth={1.25}
            className={cn('size-5 text-denim-800', (disabled || readOnly) && 'hidden')}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={Styles.countrySelectContent}>
        <Command>
          <CommandInput
            className="h-10"
            value={searchValue}
            onValueChange={newSearchValue => {
              setSearchValue(newSearchValue);
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector(
                    '[data-radix-scroll-area-viewport]'
                  );
                  if (viewportElement) {
                    viewportElement.scrollTop = 0;
                  }
                }
              }, 0);
            }}
            placeholder={t('PhoneInput.searchPlaceholder')}
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className={Styles.countrySelectScrollArea}>
              <CommandEmpty>{t('PhoneInput.empty')}</CommandEmpty>
              <CommandGroup className="p-0">
                {validCountries.map(({ value, label }) => (
                  <Option
                    key={value}
                    country={value as Country}
                    countryName={label}
                    selectedCountry={selectedCountry}
                    onChange={onChange}
                    onSelectComplete={() => setIsOpen(false)}
                  />
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
